/**
 * Licensing (docs/plans/2026-10-update-management.md, "Licensing").
 *
 * Three documents. The **enterprise licence** is the customer portal's `saa1.` token with
 * `prd: "aioe"`, held by an Enterprise AIOE and verified offline with the portal's public key
 * (the same scheme as the Cloud entitlement in billing.ts). The **device licence** is what either
 * edition hands a workbench or node: a JWS on the platform key (`/.well-known/aioe-jwks.json`),
 * which the app verifies, caches per space and gates on. The workbench mirrors this file.
 *
 *   GET  /platform/v1/licence              device or user: { token, claims } for the caller's organisation
 *   GET  /admin/v1/licence                 the console's view (LicenceView)
 *   PUT  /admin/v1/licence { token }       install or replace the enterprise licence
 *   POST /admin/v1/licence/check-in        ask the portal for a renewed token now
 */
import { z } from "zod";
export const LICENCE_PRODUCT_SLUG = "aioe";
export const DEVICE_LICENCE_TYP = "aioe-licence+jwt";
/** Seconds a device licence lives; it is refreshed on every policy pull, long before. */
export const DEVICE_LICENCE_TTL_SECONDS = 7 * 24 * 3600;
/** Days past `exp` a device keeps gating as licensed while it cannot reach its platform. */
export const DEVICE_LICENCE_GRACE_DAYS = 14;
/** Days an Enterprise AIOE runs as everything before it has a licence, from the first start of a build that knows licences. */
export const ENTERPRISE_TRIAL_DAYS = 30;
/** Days past an enterprise licence's `exp` before devices drop to Free, when the token names no `grc`. */
export const ENTERPRISE_DEFAULT_GRACE_DAYS = 30;
/**
 * Days before `exp` at which the console warns. A subscription token is short-lived by design (the
 * portal issues it for about 35 days and renews it at check-in), so only the last week counts; a
 * time-limited licence is warned about for a month.
 */
export const LICENCE_EXPIRY_WARNING_DAYS = { subscription: 7, time_limited: 30, unlimited: 0 };
export const licencePlanSchema = z.enum(["free", "teams", "enterprise"]);
/** Features a licence may name; absent on a licence means every feature of the edition. */
export const LICENCE_FEATURES = ["mesh", "sandboxed-nodes", "portfolio", "budgets", "backups", "memory", "secrets", "tools", "nodes"];
export const licenceFeatureSchema = z.enum(LICENCE_FEATURES);
/** What each plan allows a device, when the licence does not say otherwise. */
export const PLAN_FEATURES = {
    // Free is an account for a person's own machines, nothing hosted (1 Nov 2026): hosted memory is a Teams feature.
    free: [],
    teams: ["nodes", "backups", "memory", "secrets", "tools"],
    enterprise: [...LICENCE_FEATURES],
};
/**
 * The portal's `saa1` claims for `prd: "aioe"` (mju-mojoupcustomerportal `services/saa-licence.ts`),
 * plus `holder` and `seats`. Times are ISO 8601. Unknown claims are kept.
 */
export const enterpriseLicenceClaimsSchema = z
    .object({
    kid: z.string().optional(),
    /** The licence id in the portal. */
    lic: z.string().min(1),
    /** The portal organisation (the customer), as every `saa1` product carries it. */
    org: z.object({ id: z.string().optional(), name: z.string(), customerId: z.union([z.number(), z.string()]).optional() }).passthrough(),
    prd: z.literal(LICENCE_PRODUCT_SLUG),
    typ: z.enum(["subscription", "time_limited", "unlimited"]),
    /** The environment code the customer registered, when one exists. */
    env: z.string().optional(),
    /** The customer's name, for the console (equals `org.name`). */
    holder: z.string().min(1).max(200),
    /** Licensed people; absent = unlimited. */
    seats: z.number().int().min(0).optional(),
    /** The portal's feature map; for `aioe` its `features` list names what is licensed, absent = all. */
    fet: z.object({ features: z.array(z.string()).optional() }).passthrough().optional(),
    /** Supported until: releases published after this are shown, not offered. */
    sup: z.string().optional(),
    /** Days of grace past `exp`. */
    grc: z.number().int().min(0).optional(),
    chk: z.object({ mode: z.enum(["off", "soft", "renewing"]), intervalHours: z.number().int().min(1).max(24 * 30).optional(), url: z.string().url().optional() }).optional(),
    iat: z.string(),
    nbf: z.string().optional(),
    /** Absent on an unlimited licence. */
    exp: z.string().optional(),
})
    .passthrough();
/** The device licence's claims (JWS payload). Times are Unix seconds, as every platform JWS. */
export const deviceLicenceClaimsSchema = z.object({
    /** The platform's public URL. */
    iss: z.string().url(),
    /** The device id, or the person's id when a signed-in person asked. */
    sub: z.string().min(1),
    tnt: z.string().min(1),
    edition: z.enum(["enterprise", "cloud"]),
    plan: licencePlanSchema,
    features: z.array(z.string()),
    seats: z.number().int().min(0).optional(),
    /** "Mojo Up AI Cloud", or the enterprise's name. */
    holder: z.string(),
    /** Days past `exp` the app keeps gating as licensed, offline. */
    grace: z.number().int().min(0),
    /** The licence this derives from: "trial" and the day it ends, or "licence" and when it expires. */
    basis: z.object({ kind: z.enum(["plan", "trial", "licence", "lapsed"]), until: z.string().optional() }),
    iat: z.number().int(),
    exp: z.number().int(),
    jti: z.string(),
});
export const deviceLicenceResponseSchema = z.object({ token: z.string().min(1), claims: deviceLicenceClaimsSchema });
// ── Console ───────────────────────────────────────────────────────────────────
export const licenceStateSchema = z.enum(["licensed", "expiring", "grace", "lapsed", "trial", "trial-ended", "cloud"]);
/** `GET /admin/v1/licence`: what Settings → Licence shows. */
export const licenceViewSchema = z.object({
    state: licenceStateSchema,
    /** What devices are licensed as now. */
    plan: licencePlanSchema,
    holder: z.string().optional(),
    licenceId: z.string().optional(),
    type: z.enum(["subscription", "time_limited", "unlimited"]).optional(),
    seats: z.number().int().optional(),
    /** Distinct people with a workbench seen in the last 30 days. */
    peopleInUse: z.number().int(),
    nodesInUse: z.number().int(),
    features: z.array(z.string()),
    expiresAt: z.string().optional(),
    supportedUntil: z.string().optional(),
    graceDays: z.number().int(),
    /** The trial's end, when unlicensed. */
    trialEndsAt: z.string().optional(),
    installedAt: z.string().optional(),
    lastCheckInAt: z.string().optional(),
    lastCheckInError: z.string().optional(),
    checkIn: z.enum(["off", "soft", "renewing"]),
    /** A sentence for the banner when something needs an admin. */
    banner: z.object({ tone: z.enum(["warning", "danger"]), text: z.string() }).optional(),
});
export const licenceInstallSchema = z.object({ token: z.string().min(10).max(20_000) });
/** `POST /api/checkin` on the portal, `Authorization: Bearer saa1.…` (the portal also takes the token as `activationCode` in the body). */
export const licenceCheckInRequestSchema = z.object({
    product: z.literal(LICENCE_PRODUCT_SLUG),
    version: z.string(),
    people: z.number().int().nonnegative(),
    nodes: z.number().int().nonnegative(),
    workbenches: z.number().int().nonnegative(),
});
/** The portal answers with more (activationCode, renewed, licence, update); these are what AIOE reads. */
export const licenceCheckInResponseSchema = z
    .object({
    /** A renewed token, when the portal has one. */
    token: z.string().optional(),
    revoked: z.boolean().optional(),
    revokedReason: z.string().optional(),
    message: z.string().optional(),
})
    .passthrough();
//# sourceMappingURL=licence.js.map